import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../../../utils/apiAdmin';
import { adminSessionUsable } from '../../../utils/authAdmin';
import { ensureAdminWikiImageCookies } from '../../../utils/wikiImageCookies';
import WikiTreeView from '../../../components/WikiTreeView';
import Tooltip from '../../../components/Tooltip';
import WikiPageEditor from '../../../components/WikiPageEditor';
import StaleContentBanner from '../../../components/StaleContentBanner';
import {
    FaPlus,
    FaTrash,
    FaBook,
    FaSave,
    FaTimes,
    FaSpinner,
    FaExclamationTriangle,
    FaChevronDown,
    FaChevronRight
} from 'react-icons/fa';

const STORAGE_KEY = 'wikiTemplatesTabState';

/**
 * ⛔ DEBOUNCED, AND THIS IS THE ONE REFETCH IN THE PLAN THAT GETS WORSE AS THE
 * PRODUCT IS USED (LIV-13).
 *
 * `GET /admin/wiki-templates` is one of the codebase's three sanctioned
 * `DELIBERATE SCAN`s - the wiki-templates table is keyed on `wikiId` with no
 * index listing it - and a template-scope `wiki_updated` fires on every page
 * save in any client wiki template, per open tab. A scan's cost grows with the
 * table, so this would pass every test on a fixture and degrade in production.
 */
const LIVE_REFRESH_DEBOUNCE_MS = 500;

const WikiTemplatesTab = ({ searchQuery, onUnsavedChanges, liveMessages = [] }) => {
    // Load initial state from localStorage
    const getInitialState = () => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                return JSON.parse(saved);
            }
        } catch (err) {
            console.error('Failed to load wiki templates tab state:', err);
        }
        return { selectedWikiId: null, selectedPageId: null };
    };

    const initialState = getInitialState();

    // Wiki templates list state
    const [wikiTemplates, setWikiTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Selected wiki state
    const [selectedWiki, setSelectedWiki] = useState(null);
    const [pendingWikiId, setPendingWikiId] = useState(initialState.selectedWikiId);
    const [wikiDetails, setWikiDetails] = useState({ name: '', description: '' });
    const [wikiDetailsChanged, setWikiDetailsChanged] = useState(false);
    const [savingDetails, setSavingDetails] = useState(false);

    // Linked templates state
    const [availableTemplates, setAvailableTemplates] = useState([]);
    const [linkedTemplateIds, setLinkedTemplateIds] = useState([]);
    const [savingLinks, setSavingLinks] = useState(false);

    // Pages state
    const [pages, setPages] = useState([]);
    const [loadingPages, setLoadingPages] = useState(false);
    const [selectedPageId, setSelectedPageId] = useState(null);
    const [pendingPageId, setPendingPageId] = useState(initialState.selectedPageId);
    const [selectedPage, setSelectedPage] = useState(null);
    const [pageContent, setPageContent] = useState('');
    const [lastSavedContent, setLastSavedContent] = useState('');
    const [savingPage, setSavingPage] = useState(false);

    // Modal state
    const [showCreateWikiModal, setShowCreateWikiModal] = useState(false);
    const [showCreatePageModal, setShowCreatePageModal] = useState(false);
    const [createPageParentId, setCreatePageParentId] = useState(null);
    const [newWikiName, setNewWikiName] = useState('');
    const [newPageTitle, setNewPageTitle] = useState('');
    const [creating, setCreating] = useState(false);

    // Delete confirmation state
    const [showDeleteWikiConfirm, setShowDeleteWikiConfirm] = useState(null);
    const [showDeletePageConfirm, setShowDeletePageConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // Linked templates collapse state
    const [linkedTemplatesCollapsed, setLinkedTemplatesCollapsed] = useState(false);

    const editorRef = useRef(null);

    // Dirty state is derived, never set. A re-serialisation of loaded content
    // therefore cannot mark the page changed.
    const pageContentChanged = pageContent !== lastSavedContent;

    // ⚠️ SILENT MODE (LIV, D1): the message path passes `{ silent: true }` - no
    // loading state, no error banner, and the data on screen kept on failure.
    const fetchWikiTemplates = useCallback(async ({ silent = false } = {}) => {
        try {
            if (!silent) setError(null);
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/wiki-templates`);
            if (response.ok) {
                const data = await response.json();
                if (!data.wikiTemplates) {
                    throw new Error('API response missing wikiTemplates array');
                }
                setWikiTemplates(data.wikiTemplates);
            } else if (!silent) {
                const data = await response.json();
                setError(data.error ? data.error : 'Failed to fetch wiki templates');
            } else {
                console.warn('Background wiki refresh failed - keeping the list on screen');
            }
        } catch (err) {
            console.error('Failed to fetch wiki templates:', err);
            if (!silent) setError('Failed to fetch wiki templates');
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    // Fetch available main templates (for linking)
    const fetchAvailableTemplates = useCallback(async () => {
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/wiki-templates/available-templates`);
            if (response.ok) {
                const data = await response.json();
                if (!data.templates) {
                    throw new Error('API response missing templates array');
                }
                setAvailableTemplates(data.templates);
            }
        } catch (err) {
            console.error('Failed to fetch available templates:', err);
        }
    }, []);

    // Fetch pages for selected wiki
    const fetchPages = useCallback(async (wikiId, { silent = false, skipIfDirty = false } = {}) => {
        if (!wikiId) return;
        if (!silent) setLoadingPages(true);
        try {
            // Signed cookies for this template's images, scoped to
            // /wiki-images/<wikiId>/, before any page content renders.
            // Without them every image 403s, so stop rather than show a broken page.
            if (!await ensureAdminWikiImageCookies(wikiId)) {
                if (!silent) {
                    setPages([]);
                    setError('Failed to authorise wiki images');
                }
                return;
            }

            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/wiki-templates/${wikiId}/pages`);
            if (response.ok) {
                const data = await response.json();
                if (!data.pages) {
                    throw new Error('API response missing pages array');
                }
                // ⛔ RE-CHECK ON ARRIVAL - see the caller's comment.
                if (skipIfDirty && liveStateRef.current.pageContentChanged) return;
                setPages(data.pages);
            }
        } catch (err) {
            console.error('Failed to fetch pages:', err);
        } finally {
            if (!silent) setLoadingPages(false);
        }
    }, []);

    // Fetch linked templates for selected wiki
    const fetchLinkedTemplates = useCallback(async (wikiId) => {
        if (!wikiId) return;
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/wiki-templates/${wikiId}/linked-templates`);
            if (response.ok) {
                const data = await response.json();
                if (!data.linkedTemplates) {
                    throw new Error('API response missing linkedTemplates array');
                }
                setLinkedTemplateIds(data.linkedTemplates.map(t => t.templateId));
            }
        } catch (err) {
            console.error('Failed to fetch linked templates:', err);
        }
    }, []);

    // Initial load
    useEffect(() => {
        fetchWikiTemplates();
        fetchAvailableTemplates();
    }, [fetchWikiTemplates, fetchAvailableTemplates]);

    // Restore selected wiki from pending state once wikis are loaded
    useEffect(() => {
        if (pendingWikiId && wikiTemplates.length > 0 && !selectedWiki) {
            const wiki = wikiTemplates.find(w => w.wikiId === pendingWikiId);
            if (wiki) {
                setSelectedWiki(wiki);
            }
            setPendingWikiId(null);
        }
    }, [pendingWikiId, wikiTemplates, selectedWiki]);

    // Restore selected page from pending state once pages are loaded
    useEffect(() => {
        if (pendingPageId && pages.length > 0 && !selectedPageId) {
            const page = pages.find(p => p.pageId === pendingPageId);
            if (page) {
                setSelectedPageId(pendingPageId);
            }
            setPendingPageId(null);
        }
    }, [pendingPageId, pages, selectedPageId]);

    // Persist state to localStorage
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                selectedWikiId: selectedWiki?.wikiId,
                selectedPageId: selectedPageId
            }));
        } catch (err) {
            console.error('Failed to save wiki templates tab state:', err);
        }
    }, [selectedWiki, selectedPageId]);

    // Report unsaved changes to parent
    useEffect(() => {
        if (onUnsavedChanges) {
            onUnsavedChanges(pageContentChanged || wikiDetailsChanged);
        }
    }, [pageContentChanged, wikiDetailsChanged, onUnsavedChanges]);

    // When wiki selection changes
    useEffect(() => {
        if (selectedWiki) {
            setWikiDetails({ name: selectedWiki.name, description: selectedWiki.description ? selectedWiki.description : '' });
            setWikiDetailsChanged(false);
            fetchPages(selectedWiki.wikiId);
            fetchLinkedTemplates(selectedWiki.wikiId);
            setSelectedPageId(null);
            setSelectedPage(null);
            setPageContent('');
            setLastSavedContent('');
        } else {
            setPages([]);
            setLinkedTemplateIds([]);
            setSelectedPageId(null);
            setSelectedPage(null);
        }
    }, [selectedWiki, fetchPages, fetchLinkedTemplates]);

    // When page selection changes
    useEffect(() => {
        if (selectedPageId) {
            const page = pages.find(p => p.pageId === selectedPageId);
            setSelectedPage(page);
            // Empty string is valid for new pages with no content
            const content = page && page.contentHtml ? page.contentHtml : '';
            setPageContent(content);
            setLastSavedContent(content);
        } else {
            setSelectedPage(null);
            setPageContent('');
            setLastSavedContent('');
        }
    }, [selectedPageId, pages]);

    // -----------------------------------------------------------------------
    // Live updates (LIV-13, D9). The PARENT handles the socket and forwards the
    // message; this tab owns the lists and the open page, so it refetches,
    // compares and mounts the banner.
    // -----------------------------------------------------------------------

    /** "Somebody else saved this page" - D9, the same banner as D3 and D4. */
    const [stalePage, setStalePage] = useState(false);
    useEffect(() => {
        if (!pageContentChanged) setStalePage(false);
    }, [pageContentChanged]);

    // GATE 3. The in-flight flags hold the whole refresh; the DIRTY flags do
    // not, because the compare has to run precisely when the editor is dirty -
    // it is what decides whether to raise the banner.
    const liveRefreshBlocked = Boolean(savingPage || creating || deleting || savingDetails || savingLinks);
    const liveRefreshBlockedRef = useRef(liveRefreshBlocked);
    useEffect(() => { liveRefreshBlockedRef.current = liveRefreshBlocked; }, [liveRefreshBlocked]);

    const liveStateRef = useRef({});
    liveStateRef.current = { selectedWiki, selectedPage, pageContentChanged };

    // ⛔ TWO KINDS HELD SEPARATELY, NOT ONE SLOT. This tab consumes two message
    // types, and a template editor emits `template_updated` per row - so a
    // burst inside the 500ms debounce, or during a gate-3 hold, would overwrite
    // a pending `wiki_updated` and lose a page save outright.
    const pendingRef = useRef({ templates: false, wikiIds: new Set() });
    const refreshTimerRef = useRef(null);

    const hasPending = () => pendingRef.current.templates || pendingRef.current.wikiIds.size > 0;

    const runLiveRefresh = useCallback(async () => {
        // GATE 0 lives HERE - at the point of refetch, once per coalesced
        // refresh - not per message in the parent's socket handler (Fable, round
        // 2). Checked BEFORE the pending flags are taken, so a session that
        // cannot be renewed leaves them held for the next signal (D11); a
        // background request must never take apiAdmin's 401 path and redirect a
        // tab with an editor open in it.
        if (!await adminSessionUsable()) return;
        const pending = pendingRef.current;
        pendingRef.current = { templates: false, wikiIds: new Set() };
        if (!pending.templates && pending.wikiIds.size === 0) return;

        const { selectedWiki: wiki, selectedPage: page, pageContentChanged: dirty }
            = liveStateRef.current;

        if (pending.templates) {
            // The wiki-to-template LINK is written on the TEMPLATES table, so
            // these two lists ride that table's signal and not wiki_updated.
            fetchAvailableTemplates();
            if (wiki) fetchLinkedTemplates(wiki.wikiId);
        }

        if (pending.wikiIds.size === 0) return;

        fetchWikiTemplates({ silent: true });
        if (!wiki || !pending.wikiIds.has(wiki.wikiId)) return;

        if (!dirty) {
            // Clean: refresh the page list, which is what the tree and the
            // editor both read from.
            //
            // ⚠️ `skipIfDirty`: this test happened BEFORE the round trip, and
            // applying a late response would reset the editor through the
            // [selectedPageId, pages] effect - losing anything typed in
            // between. The trigger is the echo of the user's own save, so it
            // recurs.
            fetchPages(wiki.wikiId, { silent: true, skipIfDirty: true });
            return;
        }

        // ⛔ DIRTY: refetching the pages would replace `pages`, and the effect
        // that watches it would reset `pageContent` - destroying what the user
        // is typing. So read WITHOUT setting state, and compare.
        //
        // ⚠️ The comparison field is `updatedAt`, because client wiki page rows
        // carry NO version attribute at all. That makes it weaker than the
        // other two editors' checks: two saves inside the same timestamp
        // resolution read as an echo. Accepted - adding a version attribute to
        // two live tables and every write path is its own change.
        if (!page) return;
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/wiki-templates/${wiki.wikiId}/pages`);
            if (!response.ok) return;
            const data = await response.json();
            const fresh = (data.pages || []).find(p => p.pageId === page.pageId);
            if (fresh && page.updatedAt && fresh.updatedAt > page.updatedAt) {
                setStalePage(true);
            }
        } catch (err) {
            console.error('Failed to check for a newer page:', err);
        }
    }, [fetchWikiTemplates, fetchPages, fetchLinkedTemplates, fetchAvailableTemplates]);

    const scheduleLiveRefresh = useCallback(() => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(() => {
            refreshTimerRef.current = null;
            runLiveRefresh();
        }, LIVE_REFRESH_DEBOUNCE_MS);
    }, [runLiveRefresh]);

    useEffect(() => () => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    }, []);

    // The forwarded message. D11: a message arriving while an action is in
    // flight is HELD, not dropped.
    // Everything above the last sequence this tab saw. React batches, so a
    // single-message prop would collapse a burst into its last entry.
    //
    // ⚠️ SEEDED FROM THE QUEUE AT MOUNT, not from zero. The parent keeps up to
    // fifty entries and unmounts the inactive tab, so a tab switch would replay
    // every queued message - one more run of the sanctioned wiki-templates SCAN,
    // half a second after this tab's own mount fetch loaded the same data
    // (Fable, round 2). What arrived before the mount is what the mount fetched.
    const lastSeqRef = useRef(liveMessages.length ? liveMessages[liveMessages.length - 1].seq : 0);
    useEffect(() => {
        const fresh = liveMessages.filter(entry => entry.seq > lastSeqRef.current);
        if (fresh.length === 0) return;
        lastSeqRef.current = fresh[fresh.length - 1].seq;

        let queued = false;
        for (const { message } of fresh) {
            if (message.type === 'template_updated') {
                pendingRef.current.templates = true;
                queued = true;
            } else if (message.type === 'wiki_updated' && message.scope === 'template') {
                pendingRef.current.wikiIds.add(message.wikiId);
                queued = true;
            }
        }
        if (!queued || liveRefreshBlockedRef.current) return;
        scheduleLiveRefresh();
    }, [liveMessages, scheduleLiveRefresh]);

    useEffect(() => {
        if (liveRefreshBlocked || !hasPending()) return;
        scheduleLiveRefresh();
    }, [liveRefreshBlocked, scheduleLiveRefresh]);

    /** D9's Reload: discard the local edits and take the server's copy. */
    const handleStaleReload = useCallback(() => {
        const { selectedWiki: wiki } = liveStateRef.current;
        setStalePage(false);
        if (wiki) fetchPages(wiki.wikiId);
    }, [fetchPages]);

    // Filter wikis by search query
    const filteredWikis = wikiTemplates.filter(wiki => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return wiki.name.toLowerCase().includes(query) ||
               (wiki.description && wiki.description.toLowerCase().includes(query));
    });

    // Filter pages by search query
    const filteredPages = pages.filter(page => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return page.title.toLowerCase().includes(query) ||
               (page.contentHtml && page.contentHtml.toLowerCase().includes(query));
    });

    // Select wiki
    const handleSelectWiki = (wiki) => {
        if (pageContentChanged) {
            if (!window.confirm('You have unsaved changes. Discard them?')) {
                return;
            }
        }
        setSelectedWiki(wiki);
    };

    // Handle page selection with unsaved changes warning
    const handlePageSelect = useCallback((pageId) => {
        if (pageContentChanged && pageId !== selectedPageId) {
            if (!window.confirm('You have unsaved changes. Discard them?')) {
                return;
            }
        }
        setSelectedPageId(pageId);
    }, [pageContentChanged, selectedPageId]);

    // Create new wiki template
    const handleCreateWiki = async (e) => {
        e.preventDefault();
        if (!newWikiName.trim() || creating) return;

        setCreating(true);
        setError(null);
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/wiki-templates`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: newWikiName.trim() })
            });
            if (response.ok) {
                const data = await response.json();
                setShowCreateWikiModal(false);
                setNewWikiName('');
                await fetchWikiTemplates();
                setSelectedWiki(data);
            } else {
                const data = await response.json();
                setError(data.error ? data.error : 'Failed to create wiki template');
            }
        } catch (err) {
            console.error('Failed to create wiki template:', err);
            setError('Failed to create wiki template');
        } finally {
            setCreating(false);
        }
    };

    // Save wiki details
    const handleSaveDetails = async () => {
        if (!selectedWiki || savingDetails) return;

        setSavingDetails(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/wiki-templates/${selectedWiki.wikiId}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(wikiDetails)
                }
            );
            if (response.ok) {
                setWikiDetailsChanged(false);
                // Update local state
                setSelectedWiki(prev => ({ ...prev, ...wikiDetails }));
                setWikiTemplates(prev => prev.map(w =>
                    w.wikiId === selectedWiki.wikiId ? { ...w, ...wikiDetails } : w
                ));
            } else {
                const data = await response.json();
                setError(data.error ? data.error : 'Failed to save wiki details');
            }
        } catch (err) {
            console.error('Failed to save wiki details:', err);
            setError('Failed to save wiki details');
        } finally {
            setSavingDetails(false);
        }
    };

    // Delete wiki template
    const handleDeleteWiki = async () => {
        if (!showDeleteWikiConfirm || deleting) return;

        setDeleting(true);
        setDeleteError(null);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/wiki-templates/${showDeleteWikiConfirm.wikiId}`,
                { method: 'DELETE' }
            );
            if (response.ok) {
                setShowDeleteWikiConfirm(null);
                if (selectedWiki?.wikiId === showDeleteWikiConfirm.wikiId) {
                    setSelectedWiki(null);
                }
                fetchWikiTemplates();
            } else {
                const data = await response.json();
                setDeleteError(data.message ? data.message : (data.error ? data.error : 'Failed to delete wiki template'));
            }
        } catch (err) {
            console.error('Failed to delete wiki template:', err);
            setDeleteError('Failed to delete wiki template');
        } finally {
            setDeleting(false);
        }
    };

    // Toggle template link
    const handleToggleTemplateLink = async (templateId) => {
        if (!selectedWiki || savingLinks) return;

        const newLinkedIds = linkedTemplateIds.includes(templateId)
            ? linkedTemplateIds.filter(id => id !== templateId)
            : [...linkedTemplateIds, templateId];

        setSavingLinks(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/wiki-templates/${selectedWiki.wikiId}/linked-templates`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ templateIds: newLinkedIds })
                }
            );
            if (response.ok) {
                setLinkedTemplateIds(newLinkedIds);
                // Refresh available templates to update linkedWikiId
                fetchAvailableTemplates();
            } else {
                const data = await response.json();
                setError(data.error ? data.error : 'Failed to update linked templates');
            }
        } catch (err) {
            console.error('Failed to update linked templates:', err);
            setError('Failed to update linked templates');
        } finally {
            setSavingLinks(false);
        }
    };

    // Create new page
    const handleCreatePage = async (e) => {
        e.preventDefault();
        if (!selectedWiki || !newPageTitle.trim() || creating) return;

        setCreating(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/wiki-templates/${selectedWiki.wikiId}/pages`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        title: newPageTitle.trim(),
                        parentId: createPageParentId,
                        contentHtml: ''
                    })
                }
            );
            if (response.ok) {
                const data = await response.json();
                setShowCreatePageModal(false);
                setNewPageTitle('');
                setCreatePageParentId(null);
                await fetchPages(selectedWiki.wikiId);
                setSelectedPageId(data.pageId);
            } else {
                const data = await response.json();
                setError(data.error ? data.error : 'Failed to create page');
            }
        } catch (err) {
            console.error('Failed to create page:', err);
            setError('Failed to create page');
        } finally {
            setCreating(false);
        }
    };

    // Handle add child from tree context menu
    const handleAddChild = (parentId) => {
        setCreatePageParentId(parentId);
        setShowCreatePageModal(true);
    };

    // Handle delete page from tree context menu
    const handleDeletePageRequest = (pageId) => {
        const page = pages.find(p => p.pageId === pageId);
        if (page) {
            setShowDeletePageConfirm(page);
        }
    };

    // Delete page
    const handleDeletePage = async () => {
        if (!selectedWiki || !showDeletePageConfirm || deleting) return;

        setDeleting(true);
        setDeleteError(null);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/wiki-templates/${selectedWiki.wikiId}/pages/${showDeletePageConfirm.pageId}`,
                { method: 'DELETE' }
            );
            if (response.ok) {
                setShowDeletePageConfirm(null);
                if (selectedPageId === showDeletePageConfirm.pageId) {
                    setSelectedPageId(null);
                }
                fetchPages(selectedWiki.wikiId);
            } else {
                const data = await response.json();
                setDeleteError(data.message ? data.message : (data.error ? data.error : 'Failed to delete page'));
            }
        } catch (err) {
            console.error('Failed to delete page:', err);
            setDeleteError('Failed to delete page');
        } finally {
            setDeleting(false);
        }
    };

    // Handle page reorder from tree drag-drop
    const handleReorderPage = async (pageId, newParentId, newOrder) => {
        if (!selectedWiki) return;

        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/wiki-templates/${selectedWiki.wikiId}/pages/${pageId}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ parentId: newParentId, order: newOrder })
                }
            );
            if (response.ok) {
                fetchPages(selectedWiki.wikiId);
            }
        } catch (err) {
            console.error('Failed to reorder page:', err);
        }
    };

    // Handle page content change
    const handleContentChange = (html) => {
        setPageContent(html);
    };

    // Save page content
    const handleSavePage = async () => {
        if (!selectedWiki || !selectedPage || savingPage) return;

        setSavingPage(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/wiki-templates/${selectedWiki.wikiId}/pages/${selectedPage.pageId}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ contentHtml: pageContent })
                }
            );
            if (response.ok) {
                setLastSavedContent(pageContent);
                // ⛔ MERGE THE RESPONSE, do not mint a local row (D9). The route
                // re-reads and returns the written page, so the server's
                // `updatedAt` is in this body - and that value is what the echo
                // check below compares against. Merging only `contentHtml`, as
                // this did, leaves the row holding the timestamp from the last
                // LOAD, so the page's own save would come back a second later
                // looking like somebody else's write.
                const saved = await response.json().catch(() => null);
                setPages(prev => prev.map(p =>
                    p.pageId === selectedPage.pageId
                        ? { ...p, ...(saved && saved.pageId ? saved : {}), contentHtml: pageContent }
                        : p
                ));
            } else {
                const data = await response.json();
                setError(data.error ? data.error : 'Failed to save page');
            }
        } catch (err) {
            console.error('Failed to save page:', err);
            setError('Failed to save page');
        } finally {
            setSavingPage(false);
        }
    };

    // Handle image upload
    const handleImageUpload = async (file) => {
        console.log('WikiTemplatesTab.handleImageUpload called with file:', file?.name, file?.type);
        console.log('selectedWiki:', selectedWiki?.wikiId, selectedWiki?.name);

        if (!selectedWiki) {
            console.error('handleImageUpload: No wiki selected');
            return null;
        }

        try {
            console.log('Requesting presigned URL...');
            // Get presigned URL
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/wiki-images/upload-url`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    contextId: selectedWiki.wikiId,
                    filename: file.name,
                    contentType: file.type
                })
            });

            console.log('Presigned URL response status:', response.status);
            if (!response.ok) {
                const errorText = await response.text();
                console.error('Failed to get presigned URL:', errorText);
                throw new Error('Failed to get upload URL');
            }

            const { uploadUrl, imageUrl } = await response.json();
            console.log('Got presigned URL, imageUrl:', imageUrl);

            // Upload to S3
            console.log('Uploading to S3...');
            const uploadResponse = await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': file.type }
            });

            console.log('S3 upload response status:', uploadResponse.status);
            if (!uploadResponse.ok) {
                const errorText = await uploadResponse.text();
                console.error('S3 upload failed:', errorText);
                throw new Error('Failed to upload image');
            }

            // The editor inserts this src immediately, so make sure a live
            // cookie covers it - the one from page load may be near expiry on a
            // long editing session. Throwing here stops a broken image being
            // inserted; the catch below surfaces it.
            if (!await ensureAdminWikiImageCookies(selectedWiki.wikiId)) {
                throw new Error('Failed to authorise wiki images');
            }

            console.log('Image upload successful, returning:', imageUrl);
            return imageUrl;
        } catch (err) {
            console.error('Image upload failed:', err);
            setError('Failed to upload image');
            return null;
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="h-full flex items-center justify-center bg-gray-50">
                <FaSpinner className="w-8 h-8 text-primary-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className="h-full flex bg-gray-50">
            {/* Left Panel - Wiki Templates List */}
            <div className="w-64 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col">
                <div className="p-3 border-b border-gray-200">
                    <button
                        onClick={() => setShowCreateWikiModal(true)}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
                    >
                        <FaPlus className="w-3 h-3" />
                        New Wiki
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-2">
                    {filteredWikis.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">
                            {searchQuery ? 'No matching wikis' : 'No wiki templates yet'}
                        </p>
                    ) : (
                        <div className="space-y-1">
                            {filteredWikis.map(wiki => (
                                <div
                                    key={wiki.wikiId}
                                    className={`
                                        group flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer
                                        ${selectedWiki?.wikiId === wiki.wikiId
                                            ? 'bg-primary-50 border border-primary-200'
                                            : 'hover:bg-gray-100'}
                                    `}
                                    onClick={() => handleSelectWiki(wiki)}
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <FaBook className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                        <span className="text-sm text-gray-700 truncate">{wiki.name}</span>
                                    </div>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowDeleteWikiConfirm(wiki);
                                        }}
                                        className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100"
                                        title="Delete wiki"
                                    >
                                        <FaTrash className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Right Panel - Wiki Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {!selectedWiki ? (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="text-center">
                            <FaBook className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-600">Select a Wiki</h3>
                            <p className="text-sm text-gray-500 mt-2">
                                Choose a wiki template from the list or create a new one.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 flex overflow-hidden">
                        {/* Middle Panel - Wiki Details, Links, and Tree */}
                        <div className="w-80 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
                            {/* Wiki Details */}
                            <div className="p-4 border-b border-gray-200">
                                <h3 className="text-sm font-semibold text-gray-700 mb-3">Wiki Details</h3>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                                        <input
                                            type="text"
                                            value={wikiDetails.name}
                                            onChange={(e) => {
                                                setWikiDetails(prev => ({ ...prev, name: e.target.value }));
                                                setWikiDetailsChanged(true);
                                            }}
                                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                                        <textarea
                                            value={wikiDetails.description}
                                            onChange={(e) => {
                                                setWikiDetails(prev => ({ ...prev, description: e.target.value }));
                                                setWikiDetailsChanged(true);
                                            }}
                                            rows={2}
                                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500 resize-none"
                                        />
                                    </div>
                                    {wikiDetailsChanged && (
                                        <button
                                            onClick={handleSaveDetails}
                                            disabled={savingDetails}
                                            className="w-full flex items-center justify-center gap-2 px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm disabled:opacity-50"
                                        >
                                            {savingDetails ? (
                                                <FaSpinner className="w-3 h-3 animate-spin" />
                                            ) : (
                                                <FaSave className="w-3 h-3" />
                                            )}
                                            Save Details
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Pages Tree */}
                            <div className="flex-1 flex flex-col overflow-hidden min-h-0">
                                <div className="p-4 pb-2 flex items-center justify-between">
                                    <h3 className="text-sm font-semibold text-gray-700">Pages</h3>
                                    <button
                                        onClick={() => {
                                            setCreatePageParentId(null);
                                            setShowCreatePageModal(true);
                                        }}
                                        className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                                    >
                                        <FaPlus className="w-2.5 h-2.5" />
                                        New Page
                                    </button>
                                </div>
                                <div className="flex-1 overflow-y-auto px-2 pb-4 min-h-0">
                                    {loadingPages ? (
                                        <div className="flex justify-center py-4">
                                            <FaSpinner className="w-5 h-5 text-gray-400 animate-spin" />
                                        </div>
                                    ) : (
                                        <WikiTreeView
                                            pages={filteredPages}
                                            selectedPageId={selectedPageId}
                                            onSelect={handlePageSelect}
                                            onReorder={handleReorderPage}
                                            onAddChild={handleAddChild}
                                            onDelete={handleDeletePageRequest}
                                            showDraftIndicators={false}
                                            storageKey={`wikiTemplateTree_${selectedWiki.wikiId}`}
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Linked Templates - Collapsible section at bottom */}
                            <div className="border-t border-gray-200 flex-shrink-0">
                                <button
                                    onClick={() => setLinkedTemplatesCollapsed(!linkedTemplatesCollapsed)}
                                    className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                                >
                                    <h3 className="text-sm font-semibold text-gray-700">
                                        Linked Templates
                                        {linkedTemplateIds.length > 0 && (
                                            <span className="ml-2 text-xs text-gray-500 font-normal">
                                                ({linkedTemplateIds.length})
                                            </span>
                                        )}
                                    </h3>
                                    {linkedTemplatesCollapsed ? (
                                        <FaChevronRight className="w-3 h-3 text-gray-400" />
                                    ) : (
                                        <FaChevronDown className="w-3 h-3 text-gray-400" />
                                    )}
                                </button>
                                {!linkedTemplatesCollapsed && (
                                    <div className="px-4 pb-4">
                                        {availableTemplates.length === 0 ? (
                                            <p className="text-xs text-gray-500">No main templates available</p>
                                        ) : (
                                            <div className="space-y-2">
                                                {availableTemplates.map(template => (
                                                    <label
                                                        key={template.templateId}
                                                        className="flex items-center gap-2 cursor-pointer"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={linkedTemplateIds.includes(template.templateId)}
                                                            onChange={() => handleToggleTemplateLink(template.templateId)}
                                                            disabled={savingLinks || (template.linkedWikiId && template.linkedWikiId !== selectedWiki?.wikiId)}
                                                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                        />
                                                        {/* fixed: the sidebar scrolls, an absolute popup would clip */}
                                                        <Tooltip fixed text={template.name} className="min-w-0">
                                                            <span className="text-sm text-gray-700 truncate">{template.name}</span>
                                                        </Tooltip>
                                                        {template.linkedWikiId && template.linkedWikiId !== selectedWiki?.wikiId && (
                                                            <span className="text-xs text-gray-400">(linked to other)</span>
                                                        )}
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                        {savingLinks && (
                                            <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                                                <FaSpinner className="w-3 h-3 animate-spin" />
                                                Saving...
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right Panel - Page Editor */}
                        <div className="flex-1 flex flex-col overflow-hidden bg-white">
                            {!selectedPage ? (
                                <div className="flex-1 flex items-center justify-center">
                                    <div className="text-center">
                                        <FaBook className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                        <p className="text-sm text-gray-500">
                                            Select a page to edit or create a new one.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                                        <div>
                                            <h3 className="font-semibold text-gray-900">{selectedPage.title}</h3>
                                            <p className="text-xs text-gray-500">Edit page content</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {pageContentChanged && (
                                                <span className="text-xs text-orange-600">Unsaved changes</span>
                                            )}
                                            <button
                                                onClick={handleSavePage}
                                                disabled={!pageContentChanged || savingPage}
                                                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {savingPage ? (
                                                    <FaSpinner className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <FaSave className="w-4 h-4" />
                                                )}
                                                Save
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex-1 overflow-y-auto p-4" ref={editorRef}>
                                        {/* D9: this page was saved elsewhere
                                            while it was open and dirty. The
                                            typed text stays exactly as it is. */}
                                        {stalePage && (
                                            <StaleContentBanner
                                                message="This page was saved somewhere else. Your changes are still here."
                                                onReload={handleStaleReload}
                                                confirm="You have unsaved changes. Discard them and load the newer page?"
                                                busy={savingPage}
                                            />
                                        )}
                                        <WikiPageEditor
                                            content={pageContent}
                                            title={selectedPage.title}
                                            onChange={handleContentChange}
                                            onImageUpload={handleImageUpload}
                                            debounceMs={0}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Error Toast */}
            {error && (
                <div className="fixed bottom-4 right-4 bg-red-100 border border-red-300 text-red-800 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50">
                    <FaExclamationTriangle className="w-5 h-5" />
                    <span className="text-sm">{error}</span>
                    <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
                        <FaTimes className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* Create Wiki Modal */}
            {showCreateWikiModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="font-semibold text-gray-900">Create Wiki Template</h3>
                            <button
                                onClick={() => {
                                    setShowCreateWikiModal(false);
                                    setNewWikiName('');
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <FaTimes className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleCreateWiki} className="p-4">
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Wiki Name
                                </label>
                                <input
                                    type="text"
                                    value={newWikiName}
                                    onChange={(e) => setNewWikiName(e.target.value)}
                                    placeholder="e.g., Standard Migration Wiki"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                    autoFocus
                                />
                            </div>
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreateWikiModal(false);
                                        setNewWikiName('');
                                    }}
                                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={!newWikiName.trim() || creating}
                                    className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                                >
                                    {creating && <FaSpinner className="w-4 h-4 animate-spin" />}
                                    Create
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Create Page Modal */}
            {showCreatePageModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="font-semibold text-gray-900">
                                {createPageParentId ? 'Create Child Page' : 'Create Page'}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowCreatePageModal(false);
                                    setNewPageTitle('');
                                    setCreatePageParentId(null);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <FaTimes className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleCreatePage} className="p-4">
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Page Title
                                </label>
                                <input
                                    type="text"
                                    value={newPageTitle}
                                    onChange={(e) => setNewPageTitle(e.target.value)}
                                    placeholder="e.g., Getting Started"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                    autoFocus
                                />
                            </div>
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreatePageModal(false);
                                        setNewPageTitle('');
                                        setCreatePageParentId(null);
                                    }}
                                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={!newPageTitle.trim() || creating}
                                    className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                                >
                                    {creating && <FaSpinner className="w-4 h-4 animate-spin" />}
                                    Create
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Wiki Confirmation Modal */}
            {showDeleteWikiConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                        <div className="p-4 border-b border-gray-200">
                            <h3 className="font-semibold text-gray-900">Delete Wiki Template</h3>
                        </div>
                        <div className="p-4">
                            <p className="text-sm text-gray-600 mb-4">
                                Are you sure you want to delete <strong>{showDeleteWikiConfirm.name}</strong>?
                                This will also delete all pages in this wiki.
                            </p>
                            {deleteError && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <div className="flex items-start gap-2">
                                        <FaExclamationTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                                        <p className="text-sm text-red-700">{deleteError}</p>
                                    </div>
                                </div>
                            )}
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowDeleteWikiConfirm(null);
                                        setDeleteError(null);
                                    }}
                                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteWiki}
                                    disabled={deleting}
                                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                                >
                                    {deleting && <FaSpinner className="w-4 h-4 animate-spin" />}
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Page Confirmation Modal */}
            {showDeletePageConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                        <div className="p-4 border-b border-gray-200">
                            <h3 className="font-semibold text-gray-900">Delete Page</h3>
                        </div>
                        <div className="p-4">
                            <p className="text-sm text-gray-600 mb-4">
                                Are you sure you want to delete <strong>{showDeletePageConfirm.title}</strong>?
                            </p>
                            {deleteError && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <div className="flex items-start gap-2">
                                        <FaExclamationTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                                        <p className="text-sm text-red-700">{deleteError}</p>
                                    </div>
                                </div>
                            )}
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowDeletePageConfirm(null);
                                        setDeleteError(null);
                                    }}
                                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeletePage}
                                    disabled={deleting}
                                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                                >
                                    {deleting && <FaSpinner className="w-4 h-4 animate-spin" />}
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WikiTemplatesTab;
